import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Book, Smartphone, CheckCircle } from 'lucide-react';
const scrollToCompra = () => {
  const element = document.getElementById('compra');
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
};
interface Testimonial {
  id: number;
  quote: string;
  name: string;
  location: string;
  edition: string;
  editionIcon: 'book' | 'smartphone';
  avatar: string;
}
const testimonials: Testimonial[] = [{
  id: 1,
  quote: 'Este libro me hizo entender que Venezuela no es el problema, sino el terreno donde se libra una guerra energética global. Después de leerlo, ya no puedo ver las sanciones de la misma forma.',
  name: 'Miguel T.',
  location: 'Houston, TX – Estados Unidos',
  edition: 'Edición física (tapa dura)',
  editionIcon: 'book',
  avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&h=600&fit=crop'
}, {
  id: 2,
  quote: 'Por fin un análisis serio que no cae en propaganda ni en consignas. Minerales de Sangre explica cómo funcionan realmente el poder, la energía y los minerales críticos en el mundo actual.',
  name: 'Laura P.',
  location: 'Miami, FL – Estados Unidos',
  edition: 'Edición física',
  editionIcon: 'book',
  avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=600&fit=crop'
}, {
  id: 3,
  quote: 'Lo más valioso del libro es que no busca culpables fáciles. Te obliga a pensar en estructuras, incentivos y consecuencias globales. Es incómodo, pero necesario.',
  name: 'Carlos M.',
  location: 'Ciudad de México, México',
  edition: 'Edición digital',
  editionIcon: 'smartphone',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=600&fit=crop'
}, {
  id: 4,
  quote: 'Nunca había entendido la relación entre petróleo, minerales críticos e inteligencia artificial hasta leer este libro. Es una mirada profunda al siglo XXI desde Venezuela.',
  name: 'Andrea R.',
  location: 'Bogotá, Colombia',
  edition: 'Edición digital',
  editionIcon: 'smartphone',
  avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=600&fit=crop'
}, {
  id: 5,
  quote: 'No es un libro político. Es geopolítica real. Si te interesa energía, soberanía y el nuevo orden multipolar, este libro es imprescindible.',
  name: 'Luis F.',
  location: 'Córdoba, Argentina',
  edition: 'Edición digital',
  editionIcon: 'smartphone',
  avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=600&h=600&fit=crop'
}];
export function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);
  const goToSlide = (index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };
  const currentTestimonial = testimonials[currentIndex];
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  return <section className="py-20 px-6 bg-[#F9F7F1] border-t border-[#1C1917]/10" data-id="element-411">
      <div className="max-w-4xl mx-auto" data-id="element-412">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-413">
          <h2 className="text-4xl md:text-5xl font-bold text-[#7F1D1D] mb-6" data-id="element-414">
            Lo que dicen los lectores sobre Minerales de Sangre
          </h2>
          <p className="text-lg md:text-xl text-[#44403C] max-w-3xl mx-auto leading-relaxed" data-id="element-415">
            No es un libro de opinión. Es un análisis que obliga a pensar en
            sistemas, incentivos y poder real.
          </p>
        </div>

        {/* Section Title */}
        <h3 className="text-2xl font-bold text-[#1C1917] mb-12 text-center" data-id="element-416">
          Opiniones de lectores
        </h3>

        {/* Carousel Container */}
        <div className="relative mb-8" data-id="element-417">
          {/* Testimonial Slide */}
          <div className="overflow-hidden" data-id="element-418">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-419">
              <motion.div key={currentIndex} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 300,
                damping: 30
              },
              opacity: {
                duration: 0.3
              }
            }} className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-[#1C1917]/10" data-id="element-420">
                {/* Author Photo with Book - Large and Centered */}
                <div className="flex justify-center mb-8" data-id="element-421">
                  <img src={currentTestimonial.avatar} alt={currentTestimonial.name} className="w-64 h-64 md:w-80 md:h-80 rounded-2xl object-cover shadow-2xl" data-id="element-422" />
                </div>

                {/* Quote */}
                <div className="mb-6" data-id="element-423">
                  <p className="text-lg md:text-xl text-[#44403C] leading-relaxed italic text-center" data-id="element-424">
                    "{currentTestimonial.quote}"
                  </p>
                </div>

                {/* Author Info */}
                <div className="text-center" data-id="element-425">
                  <p className="font-bold text-[#1C1917] text-xl mb-2" data-id="element-426">
                    {currentTestimonial.name} 📍 {currentTestimonial.location}{' '}
                    {currentTestimonial.editionIcon === 'book' ? '📘' : '📱'}{' '}
                    {currentTestimonial.edition}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-427">
            {testimonials.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`transition-all duration-300 ${index === currentIndex ? 'w-3 h-3 bg-[#7F1D1D] rounded-full' : 'w-2.5 h-2.5 bg-[#7F1D1D]/30 rounded-full hover:bg-[#7F1D1D]/50'}`} aria-label={`Ir al testimonio ${index + 1}`} data-id="element-428" />)}
          </div>
        </div>

        {/* Availability Section */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-[#1C1917]/10" data-id="element-429">
          <h3 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-6 text-center" data-id="element-430">
            📘 Disponibilidad según tu país
          </h3>

          <div className="space-y-4 mb-8" data-id="element-431">
            <div className="flex items-start gap-3" data-id="element-432">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-433" />
              <p className="text-lg text-[#44403C]" data-id="element-434">
                <span className="font-semibold" data-id="element-435">Edición digital:</span>{' '}
                disponible en Estados Unidos y toda Latinoamérica
              </p>
            </div>

            <div className="flex items-start gap-3" data-id="element-436">
              <span className="text-2xl flex-shrink-0" data-id="element-437">🇺🇸</span>
              <p className="text-lg text-[#44403C]" data-id="element-438">
                <span className="font-semibold" data-id="element-439">
                  Edición física (tapa dura y blanda):
                </span>{' '}
                disponible solo en Estados Unidos
              </p>
            </div>
          </div>

          <div className="text-center" data-id="element-440">
            <p className="text-lg text-[#44403C] mb-6" data-id="element-441">
              Únete a los lectores que entienden el poder más allá de los
              discursos
            </p>
          </div>
        </div>
      </div>
    </section>;
}